import axios from "axios";
import JWTTokenPayload from "../interfaces/JWTTokenPayload";
import React from "react";
import apiClient from "../axiosClient";
import { GridColDef, GridColumnNode, GridColumnVisibilityModel } from "@mui/x-data-grid";
import { imageUrlPattern, pdfUrlPattern, urlPattern } from "../constants/patterns";
import Filter, { DateRange, NumberRange } from "../interfaces/filters";
import Segment from "../interfaces/segment";
import Case from "../interfaces/case";
import DispositionInput from "../interfaces/disposition_input";
import InteractionValue from "../interfaces/interactionValue";
import { CustomerDetailResponse } from "../interfaces/customerDetail";
import CaseDetail from "../interfaces/caseDetail";
import { DATE_RANGE, DISPOSITION_INPUT_TYPES, FILE_TYPES, ONE_DAY_MS, ONE_HOUR_MS, ONE_MINUTE_MS, ONE_SECOND_MS, PASSWORD_ROTATION_DURATION_IN_MS, PASSWORD_ROTATION_REMINDER_DURATION_IN_MS, RANGE } from "../constants/constants";
import PasswordExpiryTimestamps from "../interfaces/passwordExpiryTimestamps";

export const extractInteractionLevel = (disposition_order: string | undefined, start: number, end?: number) => {
    if (disposition_order) {
        if (end) {
            return disposition_order.split("-").slice(start, end).join("-");
        } else {
            return disposition_order.split("-").slice(start).join("-");
        }
    }
    return '';
}

export const formatIndianNumber = (num: string | number): string => {
    // Convert the input to a number if it's a string
    const parsedNum: number = parseFloat(num.toString());
    // Round off the number to the nearest integer
    const roundedNum: number = Math.round(parsedNum);
    // Convert the number to a string to manipulate it
    const numStr: string = roundedNum.toString();
    // Split the number into parts before and after the decimal (if any)
    const parts: string[] = numStr.split('.');
    const integerPart: string = parts[0];
    const decimalPart: string = parts.length > 1 ? parts[1] : '';
    // Function to add commas in Indian style
    function addCommasIndianStyle(number: string): string {
        const lastThree: string = number.slice(-3);
        let otherNumbers: string = number.slice(0, -3);
        if (otherNumbers !== '') {
            otherNumbers = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
        }
        return otherNumbers + (otherNumbers ? ',' : '') + lastThree;
    }
    // Format the integer part with commas in Indian style
    const formattedInteger: string = addCommasIndianStyle(integerPart);
    // Combine the formatted integer part with the decimal part (if any)
    const formattedNumber: string = formattedInteger + (decimalPart ? '.' + decimalPart : '');
    return formattedNumber;
}

// Function to get browser name
export function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Edg")) return "Edge";
    if (userAgent.includes("Chrome")) return "Chrome";
    if (userAgent.includes("Safari")) return "Safari";
    return "Unknown";
}

// Function to get the user's public IP
export async function getPublicIP() {
    try {
        const res = await axios.get("https://api64.ipify.org?format=json");
        return res.data.ip;
    } catch (error) {
        console.error("Failed to fetch IP:", error);
        return "Unknown";
    }
}

export function currentPoolMonth() {
    // Get the current year and month
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0'); // Ensure 2-digit format
	const defaultMonth = `${currentYear}-${currentMonth}`; // Format: YYYY-MM
	const currentPoolMonth = `${defaultMonth}-01`; // Format: YYYY-MM-01
    return currentPoolMonth;
}

export function getCurrentMonthDateRange() {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    const todaysDate = `${year}-${month}-${day}`;

    // Get the last day of the current month
    const lastDay = new Date(year, today.getMonth() + 1, 0).getDate();
    const endOfMonthDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

    return { todaysDate, endOfMonthDate };
}

export const decodedJWT = (): JWTTokenPayload | false => {
    const token = localStorage.getItem("token");
    if (token) {
        const base64Url = token.split(".")[1]; // Extract payload part
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const decodedPayload: JWTTokenPayload = JSON.parse(atob(base64)); // Decode and parse JSON
        return decodedPayload;
    }
    return false;
};
export function isValidJSON(jsonString: string) {
    if (jsonString) {
        try {
            JSON.parse(jsonString);
            return true;
        } catch (error) {
            return false;
        }
    }
    return false;
}

export function checkIfAudioBasedDisposition(jsonString: string) {
    if (isValidJSON(jsonString)) {
        const obj = JSON.parse(jsonString);
        return obj?.type === DISPOSITION_INPUT_TYPES.audio;
    }
    return false;
}

export function checkIfFileBasedDisposition(jsonString: string) {
    if (isValidJSON(jsonString)) {
        const obj = JSON.parse(jsonString);
        return obj?.type === DISPOSITION_INPUT_TYPES.file;
    }
    return false;
}


export function getUserName(token: JWTTokenPayload|false) {
	if (token) {
		return token.username;
	}
	return null;
}

export function getUserRole(token: JWTTokenPayload|false) {
	if (token) {
		return token.user_role;
	}
	return null;
}

export function getOwnerType(token: JWTTokenPayload|false) {
	if (token) {
		return token.owner_type;
	}
	return null;
}

export function getOwnerName(token: JWTTokenPayload|false) {
	if (token) {
		return token.owner_name;
	}
	return null;
}

export function getLastPasswordUpdatedDate(token: JWTTokenPayload|false) {
    if (token) {
        return token.last_password_updated_date;
    }
    return null;
}

export function getPdbID(token: JWTTokenPayload|false) {
    if (token) {
        return token.current_pdb_id;
    }
    return null;
}

export function getCurrentPointer() {
    const currentPointer = localStorage.getItem("currentPointer");
    if (currentPointer) {
        return parseInt(currentPointer);
    }
    return 0;
}

export function setCurrentPointerLocalStorage(value: number) {
    localStorage.setItem("currentPointer", value.toString());
}

export function getCurrentPointerTimestamp() {
    const currentPointerTimestamp = localStorage.getItem("currentPointerTimestamp");
    if (currentPointerTimestamp) {
        return currentPointerTimestamp;
    }
    return "";
}

export function setCurrentPointerTimestamp(value: string) {
    localStorage.setItem("currentPointerTimestamp", value);
}


// Function to send the login audit data
export async function sendLoginAudit() {
    const JWT :JWTTokenPayload|false= decodedJWT();
    if(!JWT){
        return ;
    }
    const username= (JWT as JWTTokenPayload ).username;
    const token = localStorage.getItem("token");
    
    const API_URL = `/api/v1/user/login_audit`;
    const requestData = {
        username,
        device_id: "",
        app_version: "",
    };
    try {
        const response = await apiClient.post(API_URL,requestData, {
            headers: {
                Authorization: `Bearer ${token}`,  // Manually set Authorization header
                "Content-Type": "application/json",
            },
        });
        if(response.status!==200){
            throw response;
        }
    } catch (error) {
        alert(`Error sending login audit:${error}`);
    }
}

export function debounce(fn: any, delay: number) {
	let timerId: NodeJS.Timeout;

	const debouncedFunction = function (...args: any[]) {
		clearTimeout(timerId);
		timerId = setTimeout(() => {
			fn(...args);
		}, delay);
	};

	// Add a cancel method
	debouncedFunction.cancel = function () {
		clearTimeout(timerId);
	};

	return debouncedFunction;
}

export const setColumnVisibility = (prefix: string, state: GridColumnVisibilityModel,
    setState: React.Dispatch<React.SetStateAction<GridColumnVisibilityModel>>, value: boolean) => {
    let keys = [`${prefix}_sub_interaction`, `${prefix}_rest_interaction`, `${prefix}_ptp_date`,
    `${prefix}_followup_date`, `${prefix}_comment`, `${prefix}_interaction_date`, `${prefix}_interaction_type`];
    if (['best', 'latest'].includes(prefix)) {
        keys.push(`${prefix}_interaction_mode`);
    }
    let obj: { [key: string]: boolean } = {};
    keys.forEach(val => {
        obj[val] = value;
    });
    setState({
        ...state,
        ...obj,
    });
};

export function validateUrl(url: string): true | string {
  if (!url?.trim()) return 'URL is required.';
  if (!urlPattern.test(url)) return 'Please enter a valid HTTP/HTTPS URL.';
  const lower = url.toLowerCase();
  if (lower.includes('<script>') || lower.includes('javascript:')) {
    return 'Malicious content detected in URL.';
  }
  return true;
}

export function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Converts a query parameters object to a URL-encoded query string
 * @param params - Object with query parameter key-value pairs
 * @returns URL-encoded query string (e.g., "key1=value1&key2=value2")
 */
export const createQueryParamV2 = (params: Record<string, any>): string => {
    const parts: string[] = [];
    
    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            return; // Skip empty values
        }
        
        const encodedKey = encodeURIComponent(key);
        
        if (Array.isArray(value)) {
            // For arrays of primitives, join with commas and encode each value
            const encodedValues = value
                .filter(v => v !== null && v !== undefined && v !== '')
                .map(v => encodeURIComponent(String(v)))
                .join(',');
            if (encodedValues) {
                parts.push(`${encodedKey}=${encodedValues}`);
            }
        } else if (typeof value === 'object') {
            // For objects, JSON stringify and encode
            parts.push(`${encodedKey}=${encodeURIComponent(JSON.stringify(value))}`);
        } else {
            // For primitives (strings, numbers, booleans), just encode
            parts.push(`${encodedKey}=${encodeURIComponent(String(value))}`);
        }
    });
    
    return parts.join('&');
};


export const createQueryParam = (
    filters: Filter[], universalSearchQuery: string, rowIndex: number, poolMonth: string, isSegmentAllocation: boolean, segments: Segment[]
) => {
    const parts: string[] = [];

    // 1) Filter out any blank keys or entirely blank values
    const sanitisedFilters = filters.filter(({ key, value }) => {
        let boolValue = key.trim() !== "";
        if (Array.isArray(value)) {
            boolValue = boolValue && value.some((v) => v.trim() !== "");
        } else if (isObject(value)) {
            boolValue = boolValue && Object.keys(value).length > 0;
        } else {
            boolValue = boolValue && value.toString().trim() !== "";
        }
        return boolValue;
    });

    for (const sanitisedFilter of sanitisedFilters) {
        const k = encodeURIComponent(sanitisedFilter.key.trim());

        if (Array.isArray(sanitisedFilter.value)) {
            // gather all non‑empty items, url‑encode each, then join with commas
            const joined = sanitisedFilter.value
                .map((v) => v.trim())
                .filter((v) => v !== "")
                .map(encodeURIComponent)
                .join(",");
            if (joined) {
                parts.push(`${k}=${joined}`);
            }
        } else if (isObject(sanitisedFilter.value)) {
            switch (sanitisedFilter.type) {
                case DATE_RANGE:
                case RANGE:
                    if (sanitisedFilter.rangeKeys) {
                        Object.values(sanitisedFilter.rangeKeys).forEach(val => {
                            if ((sanitisedFilter.value as Record<string, any>)[val.valueKey]) {
                                parts.push(`${val.key}=${(sanitisedFilter.value as Record<string, any>)[val.valueKey]}`);
                            }
                        });
                    }
                    break;
                default:
                    break;
            }
        } else {
            const v = sanitisedFilter.value.toString().trim();
            if (v) {
                parts.push(`${k}=${encodeURIComponent(v)}`);
            }
        }
    }
    if (isSegmentAllocation) {
        // only if there is no segment selected in filters i.e. filters segment_id is empty
        const segmentIdFilter = filters.find(f => f.key === 'segment_id');
        // Check if segment_id filter is not set or has empty value
        const isSegmentIdEmpty = !segmentIdFilter || (segmentIdFilter.value as string[]).length === 0;
        if (isSegmentIdEmpty){
            const finalSegmentIDs = segments.filter(val => val.is_assigned_to_user).map(val => val.segment_id);
            parts.push(`segment_id=${finalSegmentIDs.join(',')}`);
        }
    }
    // universal search
    if (universalSearchQuery.trim()) {
        parts.push(`search_value=${encodeURIComponent(universalSearchQuery.trim())}`);
    }
    if (poolMonth) {
        parts.push(`pool_month=${poolMonth}`);
    }
    if (rowIndex >= 0) {
        parts.push(`row_index=${rowIndex}`);
    }
    return parts.join("&");
};

export const handleSearchCase = async (
    filters: Filter[], universalSearchQuery: string, rowIndex: number, poolMonth: string, isSegmentAllocation: boolean, segments: Segment[],
    caseApiEndpoint: string, setLoading: React.Dispatch<React.SetStateAction<boolean>>, clearResults: () => void,
    setRowCount: React.Dispatch<React.SetStateAction<number>>, setResults: React.Dispatch<React.SetStateAction<Case[]>>
) => {
    if (isSegmentAllocation && segments.filter(val => val.is_assigned_to_user).length === 0) {
        clearResults();
        return;
    }
    const singleQueryParam = await createQueryParam(filters, universalSearchQuery, rowIndex, poolMonth, isSegmentAllocation, segments);
    try {
        setLoading(true);
        let apiUrlCase = `${caseApiEndpoint}?${singleQueryParam}`;
        const response_case = await apiClient.get(apiUrlCase);
        if (response_case.status === 200) {
            const records = Case.initialiseCase(response_case.data.cases);
            setRowCount(parseInt(response_case.data.total_count));
            setResults(records);
        }
    } catch (error: any) {
        if (error.response) {
            alert('Error response data:' + JSON.stringify(error.response.data));
        } else if (error.request) {
            alert('No response received:' + error.request);
        } else {
            alert('Error message:' + error.message);
        }
        clearResults();
    } finally {
        setLoading(false);
    }
};

export const exportGridCasesToCSV = async (
    filters: Filter[], universalSearchQuery: string, selectedPoolMonth: string, isSegmentAllocation: boolean, segments: Segment[],
    exportApiEndpoint: string, setIsDownloading: React.Dispatch<React.SetStateAction<boolean>>
) => {
    const singleQueryParam = await createQueryParam(filters, universalSearchQuery, -1, selectedPoolMonth, isSegmentAllocation, segments);
    try {
        setIsDownloading(true);
        let apiUrlCase = `${exportApiEndpoint}?${singleQueryParam}`;
        const response = await apiClient.get(apiUrlCase, {
            responseType: "blob"
        });
        if (response.status === 200) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `cases.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    } catch (error: any) {
        if (error.response) {
            alert('Error response data:' + JSON.stringify(error.response.data));
        } else if (error.request) {
            alert('No response received:' + error.request);
        } else {
            alert('Error message:' + error.message);
        }
    } finally {
        setIsDownloading(false);
    }
};

export function isGridLeafColumn(
  col: GridColumnNode
): col is GridColDef {
  return 'field' in col && !('children' in col);
}

export const getOrDefaultDispositionValue = (prefilledDispositions: Record<string, any> | null, dispositionInput: DispositionInput) => {
    const defaultValue = dispositionInput.input_type === DISPOSITION_INPUT_TYPES.audio ?
        JSON.stringify({type:DISPOSITION_INPUT_TYPES.audio,value:""}) :
            dispositionInput.input_type === DISPOSITION_INPUT_TYPES.file ?
                JSON.stringify({type:DISPOSITION_INPUT_TYPES.file,value:""}) : "";
    if (prefilledDispositions && prefilledDispositions.disposition_inputs) {
        const prefetchedValues: InteractionValue[] = JSON.parse(JSON.stringify(prefilledDispositions.disposition_inputs) || '[]');
        if (prefetchedValues.find(val => val.field_name === dispositionInput.field_name)) {
            const value = prefetchedValues.find(val => val.field_name === dispositionInput.field_name)?.field_value;
            if (value) {
                return value;
            } else {
                return defaultValue;
            }
        } else {
            return defaultValue;
        }
    }
    return defaultValue

};

export const generateTomorrowDate = () => {
    const today = new Date();
    let tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const yyyy = tomorrow.getFullYear();
    const mm = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const dd = String(tomorrow.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

export const fetchCustomerDetailData = async (
    selectedRow: Case, setCustomerDetail: React.Dispatch<React.SetStateAction<CustomerDetailResponse[]>>,
    setAdditionalContactList: React.Dispatch<React.SetStateAction<string[]>>
) => {
    try {
        const response = await apiClient.get('/api/v1/customer_detail', {
            params: {
                client_name: selectedRow.client_name,
                customer_id: selectedRow.customer_id
            }
        });	
        if (response.status === 200) {
          setCustomerDetail(response.data as CustomerDetailResponse[]);
          setAdditionalContactList(
            Array.from(new Set(
              response.data
              .filter((val: CustomerDetailResponse) => 
                      val.customer_detail.field_value && 
                        val.customer_detail.group_id === "customer" && 
                        val.field.field_value_type === "Phone Number" &&
                        val.customer_detail.score !== 0 // Added: Removes values with score = 0
                     )
                     .sort((a: CustomerDetailResponse , b: CustomerDetailResponse ) => {
                       // 1. Primary Sort: Score (Descending)
                       if (b.customer_detail.score !== a.customer_detail.score) {
                         return b.customer_detail.score - a.customer_detail.score;
                       }
                       // 2. Secondary Sort: Created Date (Descending)
                       return new Date(b.customer_detail.created_date).getTime() - 
                         new Date(a.customer_detail.created_date).getTime();
                     })
                     .map((val: CustomerDetailResponse) => val.customer_detail.field_value)
            ))
          );
        }
    } catch (error: any) {
        if (error.response) {
            alert('Error response data:' + error.response.data.message);
        } else if (error.request) {
            alert('No response received:' + error.request);
        } else {
            alert('Error message:' + error.message);
        }
    }
};

export const fetchCaseDetailData = async (selectedRow: Case, setCaseDetailData: React.Dispatch<React.SetStateAction<CaseDetail[]>>) => {
    try {
        const response = await apiClient.get('/api/v1/case_detail?case_id=' + selectedRow.case_id);	
        if (response.status === 200) {
            const caseDetailData = CaseDetail.initialiseCaseDetail(response.data);
            setCaseDetailData(caseDetailData);
        } else {
            alert("Error while loading additional data list: fetching CaseDetail data" + ", Error: " + response.data.message);
        }
    } catch (error: any) {
        if (error.response) {
            alert('Error response data:' + error.response.data.message);
        } else if (error.request) {
            alert('No response received:' + error.request);
        } else {
            alert('Error message:' + error.message);
        }
    }
};

export const buildQueryObjectFromRecord = (record: Filter[]) => {
    let queryStr = "";
    record.forEach(val => {
        if (Array.isArray(val.value) && val.value.length > 0) {
            queryStr += `${val.key}=${val.value.join(',')}&`;
        } else if ((val.value as string).length > 0) {
            queryStr += `${val.key}=${val.value}&`;
        }
    });
    return queryStr;
};

export const getAutoCallDispositionMap: () => Record<string, any> = () => {
    const newDate = generateTomorrowDate();
    return {
        "BLOCKED": {
            "disposition_order": "NotReachable-Customer_disconnected_the_call",
            "disposition_inputs": [{ field_name: "followup_date", field_value: newDate }],
            "comment": "Customer has blocked this number",
            "auto_submit": true
        },
        "NO_ANSWER_TIME_EXCEEDED": {
            "disposition_order": "NotReachable-RNR",
            "disposition_inputs": [{ field_name: "followup_date", field_value: newDate }],
            "comment": "This number was not connected",
            "auto_submit": true
        },
        "SWITCHED_OFF": {
            "disposition_order": "NotReachable-Switched_Off",
            "disposition_inputs": [{ field_name: "followup_date", field_value: newDate }],
            "comment": "This number is switched off",
            "auto_submit": true
        },
        "WRONG_NUMBER": {
            "disposition_order": "NotReachable-Wrong_Number",
            "disposition_inputs": [],
            "comment": "This number doesn't exist",
            "auto_submit": true
        },
        "REJECTED_BY_USER": {
            "disposition_order": "NotReachable-Customer_disconnected_the_call",
            "disposition_inputs": [{ field_name: "followup_date", field_value: newDate }],
            "comment": "Customer has rejected the call",
            "auto_submit": true
        },
        "DECLINED_BY_PHONE": {
            "disposition_order": "NotReachable-Customer_disconnected_the_call",
            "disposition_inputs": [{ field_name: "followup_date", field_value: newDate }],
            "comment": "Customer's phone has declined the call",
            "auto_submit": true
        },
    };
};

export const generateCurrentDate = () => {
    const date = new Date();
    return date.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
};

export const isObject = (val: any) => {
	return val !== null && typeof val === 'object' && !Array.isArray(val);
}

//Checks if a filter has a non-empty value
export const hasFilterValue = (filter: Filter): boolean => {
	if (Array.isArray(filter.value)) {
		return filter.value.some(v => v && v.toString().trim() !== '');
	} else if (typeof filter.value === 'object' && filter.value !== null) {
		return Object.keys(filter.value).length > 0;
	} else {
		return Boolean(filter.value && filter.value.toString().trim() !== '');
	}
}

/**
 * Processes filter(V1) and adds its parameters to the params object
 * Handles arrays, DATE_RANGE, RANGE, and simple string/number values
 * @param filter - Filter to process
 * @param params - Params object to add entries to
 */
export const processFilterToParams = (filter: Filter, params: Record<string, any>): void => {
	if (!filter.key || !filter.key.trim()) {
		return;
	}

	if (!hasFilterValue(filter)) {
		return;
	}

	const key = filter.key.trim();

	// Handle different filter types
	if (Array.isArray(filter.value)) {
		// Array values - keep as array
		const nonEmptyValues = filter.value
			.map(v => v.toString().trim())
			.filter(v => v !== '');
		if (nonEmptyValues.length > 0) {
			params[key] = nonEmptyValues;
		}
	} else if (filter.type === DATE_RANGE && typeof filter.value === 'object') {
		// Date range filters
		const dateRange = filter.value as DateRange;
		if (filter.rangeKeys) {
			if (dateRange.startDate) {
				params[filter.rangeKeys.startKey.key] = dateRange.startDate;
			}
			if (dateRange.endDate) {
				params[filter.rangeKeys.endKey.key] = dateRange.endDate;
			}
		}
	} else if (filter.type === RANGE && typeof filter.value === 'object') {
		// Range filters (min/max)
		const rangeValue = filter.value as NumberRange;
		if (filter.rangeKeys) {
			if (rangeValue.min && rangeValue.min.trim()) {
				params[filter.rangeKeys.startKey.key] = rangeValue.min.trim();
			}
			if (rangeValue.max && rangeValue.max.trim()) {
				params[filter.rangeKeys.endKey.key] = rangeValue.max.trim();
			}
		}
	} else {
		// Simple string/number values
		const value = filter.value.toString().trim();
		if (value) {
			params[key] = value;
		}
	}
}

export const checkFileTypeFromUrl = (url: string) => {
    if (imageUrlPattern.test(url)) {
        return FILE_TYPES.image;
    } else if (pdfUrlPattern.test(url)) {
        return FILE_TYPES.pdf;
    } else {
        return FILE_TYPES.unknown;
    }
};

export const getReminderText = (timeDiff: number) => {
    return `You have ${
        timeDiff >= ONE_DAY_MS ? `${(timeDiff / ONE_DAY_MS).toFixed(0)} days` :
        timeDiff >= ONE_HOUR_MS ? `${(timeDiff / ONE_HOUR_MS).toFixed(0)} hours` :
        timeDiff >= ONE_MINUTE_MS ? `${(timeDiff / ONE_MINUTE_MS).toFixed(0)} minutes` :
        timeDiff >= ONE_SECOND_MS ? `${(timeDiff / ONE_SECOND_MS).toFixed(0)} seconds` : '0 seconds'
    } left to reset your password. Please reset your password before then.`
};

export const apiCallWrapper = async (asyncAPICall: () => Promise<any>) => {
    try {
        const response = await asyncAPICall();
        return response;
    } catch (error: any) {
        if (error.response) {
            if (error.response.status !== 404) {
                alert('Error response data:' + error.response.data.message);
            }
        } else if (error.request) {
            alert('No response received:' + error.request);
        } else {
            alert('Error message:' + error.message);
        }
        throw error;
    }
};

export const getPasswordExpiryTimestamps = (token: false | JWTTokenPayload): PasswordExpiryTimestamps => {
    const lastPasswordUpdatedDate = getLastPasswordUpdatedDate(token) || '';
    const lastPasswordUpdatedTimestamp = (new Date(lastPasswordUpdatedDate)).getTime();
    const passwordExpiryDate = new Date(lastPasswordUpdatedTimestamp + PASSWORD_ROTATION_DURATION_IN_MS);
    const passwordExpiryReminderDate = new Date(lastPasswordUpdatedTimestamp + PASSWORD_ROTATION_REMINDER_DURATION_IN_MS);
    return { passwordExpiryDate, passwordExpiryReminderDate };
};

export const formatNumberToIndianNotation = (value: number | null) => {
    if (value !== null) {
        return new Intl.NumberFormat('en-IN').format(Math.trunc(value));
    }
    return null;
};
